<template>
  <div class='cloud-asset-issue-body'>
    <div class="description-block" :style="`flex-wrap: ${isShowAll ? 'wrap' : 'nowrap'};`">
      <div class="data-title">{{$t('detailed.issue.description.title')}}</div>
      <div class="text-container description" :class='{closed: !isShowAll}'>
        {{description}}
      </div>
      <span class='hide-description-btn' @click.stop="isShowAll = !isShowAll">
          {{ captionOfDescriptionToggleButton }}
      </span>
    </div>
    <div class="asset-block">
      <div class="data-title">{{$t('detailed.issue.asset.title')}}</div>
      <v-badge
        tile
        overlap
        :icon="badgeIcon"
        :content="badgeContent"
        class="manual-config-badge"
        :class="{'copied-badge': displayedCopiedBadge}"
        offset-y='4px'
        :value="displayedCopiedBadge"
      >
        <div class="text-container asset-data-text" @click='copyAsset'>{{asset}}</div>
      </v-badge>
    </div>
    <div class="tld-block">
      <div class="data-title">{{$t('detailed.issue.tld.title')}}</div>
      <div class="text-container">
        {{tld}}
      </div>
    </div>
    <span class="hackers-view-block" v-if='hackersView && hackersView.length'>
      <div class="data-title">{{$t('detailed.issue.hackers.view.title')}}</div>
        <div v-for='(item, index) in hackersView' :key='index'>
         <div class='hackers-view-option'>
          <div class='data-title option'>
            <span class="material-icons bullet">
              lens_blur
            </span>
          </div>
           <div class="hackers-view-option-container">
             <span class="hackers-view-title">{{item.split(':')[0]}}:</span>
             <span class='text-container'>{{item.split(':')[1]}}</span>
           </div>
        </div>
      </div>
    </span>
    <span class="mitigation-block" v-if='mitigationData && mitigationData.length'>
      <div class="data-title">{{$t('detailed.issue.mitigation.title')}}</div>
        <div class="mitigation-section-container" v-for='(mitigationSection, index) in mitigationData' :key='index'>
          <div class="data-sub-title">{{mitigationDataTitles[index]}}</div>
          <div v-for='(mitigation, key) in mitigationSection' :key='key'>
             <div class='mitigation-option'>
              <div class='data-title option'>
                <span class="material-icons bullet">
                  lens_blur
                </span>
              </div>
               <div class="mitigation-option-container">
                 <span class="mitigation-title">{{mitigation.split(':')[0]}}:</span>
                 <span class='text-container'>{{mitigation.split(':')[1]}}</span>
               </div>
            </div>
          </div>
      </div>
    </span>
    <div class="technical-block" v-if="mitigationUrl">
      <div class="data-title">{{$t('issueDataPopup.technical.data')}}</div>
      <div class="technical-data">
        <div class='text-container'>
          <div class='details-column technical-data'>
            <div class="data-sub-title">{{$t('issueDataPopup.url')}}</div>
            <div class='text-container cve-url' :title='mitigationUrl'>
              <a :href='mitigationUrl' target="_blank"> {{mitigationUrl.replace(/%20/g, '-')}} </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CloudAssetIssueBody',
  props: {
    issue: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      displayedCopiedBadge: false,
      isShowAll: false,
    };
  },
  computed: {
    tld() {
      return this.issue.tld;
    },
    asset() {
      return this.issue.asset;
    },
    metaData() {
      return this.issue.metaData;
    },
    description() {
      return this.issue.description;
    },
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
    mitigationUrl() {
      return this.metaData.mitigation !== '' ? this.metaData.mitigation : null;
    },
    captionOfDescriptionToggleButton() {
      return this.isShowAll ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    hackersView() {
      return this.issue.metaData?.hackersView || null;
    },
    mitigationData() {
      return this.metaData?.mitigationData || null;
    },
    mitigationDataTitles() {
      return [this.$t('cloud.asset.issue.mitigation.access.control'),
        this.$t('cloud.asset.issue.mitigation.data.security'),
        this.$t('cloud.asset.issue.mitigation.monitoring.logging'),
        this.$t('cloud.asset.issue.mitigation.vulnerability.management')];
    },
  },
  methods: {
    async copyAsset() {
      const data = this.asset;
      await navigator.clipboard.writeText(data);
      this.displayedCopiedBadge = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge = false;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.cloud-asset-issue-body {
  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(650);
    margin-right: $space-4;
  }
  .description-block {
    display: block;
    width: rem(650);
    margin-bottom: $space-5;
    .text-container::first-letter {
      text-transform: capitalize;
    }
    .text-container {
      padding-right: rem(60);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      &.closed {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        line-height: 1.2em;
        height: 3.6em;
      }
    }
  }
  .technical-block {
    padding-bottom: $space-5;
    display: block;
    .technical-data {
      padding-top: rem(4);
      .url {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
  .mitigation-block {
    display: block;
    padding-bottom: $space-5;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
      padding-bottom: $space-4;
    }
    .mitigation-section-container {
      .data-sub-title {
        padding-top: $space-2;
      }
      .mitigation-option {
        display: flex;
        .mitigation-option-container {
          display: inline-block;
          padding-left: rem(5);
          .mitigation-title {
            font-weight: bold;
            font-size: rem(13);
          }
        }
      }
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .hackers-view-block {
    display: block;
    padding-bottom: $space-5;
    width: rem(650);
    .bullet {
      flex: 1;
    }
    .text-container {
      padding-right: rem(60);
      color: var(--text-primary);
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(18);
    }
    .hackers-view-option {
      display: flex;
      padding-bottom: $space-1;
      .hackers-view-option-container {
        display: inline-block;
        padding-left: rem(5);
        .hackers-view-title {
          font-weight: bold;
          font-size: rem(13);
        }
      }
    }
    .material-icons {
      font-size: rem(15);
      opacity: 0.5;
      margin-top: rem(3);
    }
  }
  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: rem(0.8);
    line-height: $font-size-xs;
    text-transform: uppercase;
    margin-bottom: $space-1;
  }
  .asset-block {
    margin-bottom: $space-5;
    .manual-config-badge {
      max-width: 100%;
      line-height: 1.5;
      &:hover {
        cursor: pointer;
      }
      .asset-data-text {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .copied {
        text-transform: capitalize;
        color: $default-button-color;
      }
    }
  }
  .tld-block {
    margin-bottom: $space-5;
  }
  .description {
    white-space: pre-line;
  }
  .hide-description-btn {
    color: var(--link-primary);
    font-family: $font-family-regular;
    font-size: $font-size-base;
    letter-spacing: 0;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.cloud-asset-issue-body {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge {
        background: transparent;
        border: none;
        color: var(--text-primary);
        .v-icon {
          font-size: $font-size-md;
          transform: rotate(180deg);
        }
      }
    }
    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge {
          text-transform: capitalize;
          background: transparent;
          border: none;
          color: var(--text-primary);
          right: $space-5;
          inset: auto auto calc(100% - #{rem(1)}) calc(100% - #{rem(30)});
        }
      }
    }
  }
}
</style>
